// src/automation/components/AutomationTaskView.js
//
// A clean, scrollable task page with PER-BLOCK inline editing:
//   • KPI strip        — non-collapsible read-only tiles
//   • Description      — non-collapsible one-line summary
//   • General          — editable block (label/enabled/schedule/minutes/section…)
//   • Each Trigger     — editable block (audience: email or slack fields)
//   • Advanced blocks  — Extraction / RCC / ARRM / Comparison / Reset / Nudge /
//                        queue_types / groups — each independently editable
//   • Mapping          — editable list of CSV files
//
// Every block has its own Edit button; editing a block shows typed field inputs
// and Save/Cancel. Saving merges that block into the FULL config and persists it
// to S3 via saveTaskConfig (the backend replaces the whole file, so we always
// send the complete merged object — no field loss).

import React, { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Send, Play, Zap, RefreshCw, CheckCircle2, XCircle, Plus, Trash2 } from "lucide-react";
import {
  classifyTask,
  prettyTaskLabel,
  getTriggers,
  saveTaskConfig,
  runTaskTest,
  runRapidTest,
  runTask,
  getTaskTables,
} from "../api/automation_data";
import EditableBlock, { AutoField } from "./EditableBlock";
import MappingEditor from "./MappingEditor";
import ReportDesignBlock from "./ReportDesignBlock";
import KycReportDesignBlock from "./KycReportDesignBlock";
import TableReportDesignBlock from "./TableReportDesignBlock";
import RapidAudit from "./RapidAudit";
import TemplateResetAudit from "./TemplateResetAudit";
import ScheduleEditor, {
  cleanSchedules, defaultSchedules, describeSchedules, legacyToSchedules, shortSchedules,
  taskRunsPerDay, triggerSchedule,
} from "./ScheduleEditor";

// Table-style reports that use the generic, schema-driven Report Design.
const TABLE_DESIGN_TASKS = new Set([
  "vat_hourly_qsr", "kyc_concentrix_mail", "kyc_sutherland_mail", "svr_sutherland_mail",
]);
// KYC-style reports (tables of functions × languages) that use KycReportDesignBlock.
// Matched case-insensitively: UI-added automations keep the id they were registered with.
const KYC_DESIGN_TASKS = new Set(["kyc_core_mail", "tax_queue_status_email"]);
const isKycDesign = (name) => KYC_DESIGN_TASKS.has(String(name || "").toLowerCase());
// Tasks that save a full report per run (Audit tab = TemplateResetAudit).
const RUN_AUDIT_TASKS = new Set(["kyc_template_reset"]);
const hasRunAuditTab = (name) => RUN_AUDIT_TASKS.has(String(name || "").toLowerCase());

// Pick report tables. value [] = all tables. Options come from list_tables
// ({key, title, group}); a saved key that no longer exists is shown as missing.
function TablePicker({ options, value, onChange, allLabel = "All tables", disabled = false }) {
  const sel = new Set(value || []);
  const all = sel.size === 0;
  const groups = [];
  for (const o of options) {
    let g = groups.find((x) => x.name === o.group);
    if (!g) groups.push((g = { name: o.group, items: [] }));
    g.items.push(o);
  }
  const known = new Set(options.map((o) => o.key));
  const missing = [...sel].filter((k) => !known.has(k));
  const toggle = (key) => {
    const next = new Set(sel);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const arr = options.map((o) => o.key).filter((k) => next.has(k));
    onChange(arr.length === options.length ? [] : arr);
  };
  return (
    <div className="auto-tblpick">
      <button
        type="button"
        className={`auto-sched2-chip ${all ? "is-on" : ""}`}
        aria-pressed={all}
        disabled={disabled}
        onClick={() => onChange([])}
      >
        {allLabel}
      </button>
      {groups.map((g) => (
        <div key={g.name || "_"} className="auto-tblpick-group">
          {groups.length > 1 && <span className="auto-tblpick-h">{g.name}</span>}
          {g.items.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`auto-sched2-chip ${!all && sel.has(o.key) ? "is-on" : ""}`}
              aria-pressed={!all && sel.has(o.key)}
              disabled={disabled}
              title={o.key}
              onClick={() => toggle(o.key)}
            >
              {o.title}
            </button>
          ))}
        </div>
      ))}
      {missing.length > 0 && (
        <span className="auto-sched2-error">
          No longer in the report (ignored): {missing.join(", ")}
        </span>
      )}
    </div>
  );
}

// The trigger's schedule — the ONLY schedule a task has. Unticked = this
// trigger runs only manually / on Test.
function TriggerScheduleField({ draft, setDraft }) {
  const on = !!draft.schedules;
  return (
    <div className="auto-trig-channel auto-eb-field--wide">
      <span className="auto-trig-channel-h">Schedule</span>
      <label className="auto-eb-toggle">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) =>
            setDraft({
              ...draft,
              schedules: e.target.checked ? draft._schedCache || defaultSchedules() : undefined,
              _schedCache: e.target.checked ? draft._schedCache : draft.schedules,
            })
          }
        />
        <span>Run this trigger on a schedule</span>
      </label>
      <span className="auto-eb-field-hint">
        {on
          ? "This trigger sends on the days and times below. Each trigger has its own schedule (e.g. email at 09:00, Slack hourly)."
          : "Not scheduled: this trigger only sends on a manual run or a Test."}
      </span>
      {on && <ScheduleEditor value={draft.schedules} onChange={(s) => setDraft({ ...draft, schedules: s })} />}
    </div>
  );
}

// ── trigger (audience) editors — proper fields, not JSON ────────────────────────

// A labeled recipient textarea (one email per line <-> semicolon string).
function RecipientField({ label, value, onChange }) {
  const perLine = String(value ?? "")
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");
  const count = perLine ? perLine.split("\n").length : 0;
  return (
    <label className="auto-eb-field auto-eb-field--wide">
      <span className="auto-eb-field-label">{label}</span>
      <textarea
        className="auto-eb-textarea auto-eb-textarea--emails"
        rows={Math.min(10, Math.max(3, count + 1))}
        value={perLine}
        placeholder="one email per line"
        onChange={(e) =>
          onChange(
            e.target.value
              .split(/[\n;,]/)
              .map((s) => s.trim())
              .filter(Boolean)
              .join(";")
          )
        }
      />
      <span className="auto-eb-field-hint">
        {count} recipient{count === 1 ? "" : "s"} · one per line
      </span>
    </label>
  );
}

function LineField({ label, value, onChange, wide }) {
  return (
    <label className={`auto-eb-field ${wide ? "auto-eb-field--wide" : ""}`}>
      <span className="auto-eb-field-label">{label}</span>
      <input
        className="auto-eb-input"
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

// Editor for one email audience block (to/cc/bcc/subject).
function EmailAudienceEditor({ email, onChange, title }) {
  const upd = (patch) => onChange({ ...email, ...patch });
  return (
    <div className="auto-eb-subgroup">
      {title && <h5 className="auto-eb-subtitle">{title}</h5>}
      <LineField label="Subject" value={email.subject} onChange={(v) => upd({ subject: v })} wide />
      <RecipientField label="To" value={email.to} onChange={(v) => upd({ to: v })} />
      <RecipientField label="Cc" value={email.cc} onChange={(v) => upd({ cc: v })} />
      <RecipientField label="Bcc" value={email.bcc} onChange={(v) => upd({ bcc: v })} />
    </div>
  );
}

// Delivery channels: a trigger can deliver by Email AND/OR Slack — the two are
// INDEPENDENT. Each checkbox adds/removes its delivery block. Turning a channel
// off caches its data so toggling back within the same edit restores it.
function DeliveryChannelToggles({ draft, setDraft }) {
  const hasEmail = !!draft.email;
  const hasSlack = !!draft.slack;
  const toggleEmail = () => {
    if (hasEmail) {
      setDraft({ ...draft, _emailCache: draft.email, email: undefined });
    } else {
      setDraft({
        ...draft,
        email: draft._emailCache || { from_address: "", mode: "flat", subject: "", to: "", cc: "", bcc: "" },
      });
    }
  };
  const toggleSlack = () => {
    if (hasSlack) {
      setDraft({ ...draft, _slackCache: draft.slack, slack: undefined });
    } else {
      setDraft({ ...draft, slack: draft._slackCache || { channel_id: "", comment: "" } });
    }
  };
  return (
    <div className="auto-eb-field auto-eb-field--wide">
      <span className="auto-eb-field-label">delivery channels</span>
      <div className="auto-delivery-checks">
        <label className="auto-eb-toggle">
          <input type="checkbox" checked={hasEmail} onChange={toggleEmail} />
          <span>Send email</span>
        </label>
        <label className="auto-eb-toggle">
          <input type="checkbox" checked={hasSlack} onChange={toggleSlack} />
          <span>Send to Slack</span>
        </label>
      </div>
      <span className="auto-eb-field-hint">
        {hasEmail && hasSlack
          ? "This trigger sends the SAME report to both email recipients and the Slack channel."
          : hasEmail
          ? "Email only — HTML report to recipients."
          : hasSlack
          ? "Slack only — one PNG per table posted to the channel."
          : "No channel selected — this trigger will deliver nothing."}
      </span>
    </div>
  );
}

// Full trigger editor. A trigger can deliver by Email AND/OR Slack — both blocks
// render when enabled, driven by the independent channel toggles.
function TriggerEdit({ draft, setDraft, tableOptions = null }) {
  const email = draft.email;
  const slack = draft.slack;

  // Email sub-editor (tiered or flat).
  const renderEmail = () => {
    const tiers = email.tiers || {};
    const hasTierData = (t) => t && (t.to || t.cc || t.bcc || t.subject);
    const tiered =
      email.mode === "tiered" ||
      (!email.mode && (hasTierData(tiers.t1_t2) || hasTierData(tiers.t3_t4) || hasTierData(email.t1_t2) || hasTierData(email.t3_t4)));
    const updEmail = (patch) => setDraft({ ...draft, email: { ...email, ...patch } });
    const getTier = (tk) => tiers[tk] || email[tk] || {};
    const setTier = (tk, val) => updEmail({ tiers: { ...tiers, [tk]: val } });
    return (
      <>
        <LineField label="From" value={email.from_address} onChange={(v) => updEmail({ from_address: v })} wide />
        {tiered ? (
          <>
            <EmailAudienceEditor title="Tier 1 / Tier 2" email={getTier("t1_t2")} onChange={(v) => setTier("t1_t2", v)} />
            <EmailAudienceEditor title="Tier 3 / Tier 4" email={getTier("t3_t4")} onChange={(v) => setTier("t3_t4", v)} />
          </>
        ) : (
          <EmailAudienceEditor email={email} onChange={(e) => setDraft({ ...draft, email: e })} />
        )}
      </>
    );
  };

  // Slack sub-editor.
  const renderSlack = () => {
    const updSlack = (patch) => setDraft({ ...draft, slack: { ...slack, ...patch } });
    return (
      <>
        <LineField label="Channel ID" value={slack.channel_id} onChange={(v) => updSlack({ channel_id: v })} />
        <LineField label="Comment" value={slack.comment} onChange={(v) => updSlack({ comment: v })} wide />
        <label className="auto-eb-toggle auto-eb-field--wide">
          <input
            type="checkbox"
            checked={!!slack.attach_csv}
            onChange={(e) => updSlack({ attach_csv: e.target.checked || undefined })}
          />
          <span>Also attach the detail CSV (off = table images only)</span>
        </label>
      </>
    );
  };

  return (
    <div className="auto-eb-grid">
      <LineField label="Trigger id" value={draft.id} onChange={(v) => setDraft({ ...draft, id: v })} />
      <DeliveryChannelToggles draft={draft} setDraft={setDraft} />
      {email && (
        <div className="auto-trig-channel auto-eb-field--wide">
          <span className="auto-trig-channel-h">Email</span>
          {renderEmail()}
        </div>
      )}
      {slack && (
        <div className="auto-trig-channel auto-eb-field--wide">
          <span className="auto-trig-channel-h">Slack</span>
          {renderSlack()}
        </div>
      )}
      {tableOptions && tableOptions.length > 0 && (
        <div className="auto-trig-channel auto-eb-field--wide">
          <span className="auto-trig-channel-h">Tables</span>
          <span className="auto-eb-field-hint">
            Which tables this trigger sends (email and Slack). All = the whole report, including tables added later.
          </span>
          <TablePicker
            options={tableOptions}
            value={draft.tables || []}
            onChange={(v) => {
              const next = { ...draft };
              if (v.length) next.tables = v;
              else delete next.tables;
              setDraft(next);
            }}
          />
        </div>
      )}
      <TriggerScheduleField draft={draft} setDraft={setDraft} />
    </div>
  );
}

// Read-only trigger view.
function TriggerView({ trig, tableOptions = null }) {
  const email = trig.email;
  const slack = trig.slack;
  const Row = ({ k, v }) => (
    <div className="auto-cfg-kv-row">
      <dt>{k}</dt>
      <dd>{v || "—"}</dd>
    </div>
  );
  const scheduleRow = (
    <dl className="auto-cfg-kv">
      <Row
        k="schedule"
        v={trig.schedules ? describeSchedules(trig.schedules) : "Not scheduled (manual / Test only)"}
      />
      {tableOptions && tableOptions.length > 0 && (
        <Row
          k="tables"
          v={
            trig.tables && trig.tables.length
              ? trig.tables.map((k) => tableOptions.find((o) => o.key === k)?.title || `${k} (missing)`).join(", ")
              : "All tables"
          }
        />
      )}
      {trig._inheritedSchedule && (
        <Row k="" v="Taken from the old task-level schedule — it's stored on this trigger on the next save." />
      )}
    </dl>
  );
  if (!email && !slack) {
    return (
      <>
        <div className="auto-empty">No delivery configured.</div>
        {scheduleRow}
      </>
    );
  }

  const tiers = email?.tiers || {};
  const hasTierData = (t) => t && (t.to || t.cc || t.bcc || t.subject);
  const tiered =
    email &&
    (email.mode === "tiered" ||
      (!email.mode && (hasTierData(tiers.t1_t2) || hasTierData(tiers.t3_t4) || hasTierData(email.t1_t2) || hasTierData(email.t3_t4))));
  const tierLabel = { t1_t2: "T1/T2", t3_t4: "T3/T4" };

  return (
    <>
      {email && (
        <dl className="auto-cfg-kv">
          <Row k="delivery" v="Email" />
          <Row k="from" v={email.from_address} />
          {tiered ? (
            ["t1_t2", "t3_t4"].map((tk) => {
              const t = tiers[tk] || email[tk] || {};
              return (
                <React.Fragment key={tk}>
                  <Row k={`${tierLabel[tk]} · subject`} v={t.subject} />
                  <Row k={`${tierLabel[tk]} · to`} v={t.to} />
                  <Row k={`${tierLabel[tk]} · cc`} v={t.cc} />
                  <Row k={`${tierLabel[tk]} · bcc`} v={t.bcc} />
                </React.Fragment>
              );
            })
          ) : (
            <>
              <Row k="subject" v={email.subject} />
              <Row k="to" v={email.to} />
              <Row k="cc" v={email.cc} />
              <Row k="bcc" v={email.bcc} />
            </>
          )}
        </dl>
      )}
      {slack && (
        <dl className="auto-cfg-kv">
          <Row k="delivery" v="Slack" />
          <Row k="channel_id" v={slack.channel_id} />
          {slack.comment ? <Row k="comment" v={slack.comment} /> : null}
          <Row k="attachments" v={slack.attach_csv ? "Table images + detail CSV" : "Table images only"} />
        </dl>
      )}
      {scheduleRow}
    </>
  );
}

// ── KPI helpers ────────────────────────────────────────────────────────────────

function prettyFrequency(schedule) {
  // Just-saved value may still be a spec object until the config is re-read.
  if (schedule && typeof schedule === "object") {
    if (schedule.type === "hourly") return "Every hour";
    if (schedule.type === "every_minutes") return `Every ${schedule.minutes} min`;
    if (schedule.type === "daily")
      return `Daily at ${String(schedule.hour).padStart(2, "0")}:${String(schedule.minute).padStart(2, "0")} UTC`;
    return "Custom schedule";
  }
  if (!schedule || typeof schedule !== "string") return "—";
  const rate = schedule.match(/^rate\((.+)\)$/);
  if (rate) return `Every ${rate[1]}`;
  const cron = schedule.match(/^cron\((.+)\)$/);
  if (cron) {
    const parts = cron[1].trim().split(/\s+/);
    const [minute, hour] = parts;
    if (hour === "*" && minute === "0") return "Every hour";
    if (hour === "*" && /^0\/\d+$/.test(minute)) return `Every ${minute.slice(2)} min`;
    if (/^\d+$/.test(minute) && /^\d+$/.test(hour))
      return `Daily at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} UTC`;
    if (hour === "*" && /^\d+$/.test(minute)) return `Hourly at :${String(minute).padStart(2, "0")}`;
    return "Custom schedule";
  }
  return schedule;
}
function deliveryCount(cfg) {
  if (Array.isArray(cfg.groups) && cfg.groups.length) return cfg.groups.length;
  return getTriggers(cfg).filter((t) => t.email || t.slack).length;
}
function timeSaved(cfg, totalRuns) {
  const per = Number(cfg.minutes_saved_per_run);
  if (!Number.isFinite(per) || totalRuns == null) return null;
  return per * totalRuns;
}
function formatMinutes(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const hrs = mins / 60;
  if (hrs < 24) return `${hrs.toFixed(1)} hr`;
  return `${(hrs / 24).toFixed(1)} d`;
}
const TYPE_META = {
  mail: { label: "Mail", cls: "auto-type--mail" },
  slack: { label: "Slack", cls: "auto-type--slack" },
  rapid: { label: "Rapid", cls: "auto-type--rapid" },
  forecast: { label: "Forecast", cls: "auto-type--forecast" },
  generic: { label: "Automation", cls: "auto-type--generic" },
};

// Format a config start_date ("YYYY-MM-DD" or ISO) as "19 Oct 2026". Falls back
// to an em dash when the task has no start_date yet.
function formatStartDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function Kpi({ label, value, hint, children }) {
  return (
    <div className="auto-kpi">
      {children ? children : <span className="auto-kpi-value">{value}</span>}
      <span className="auto-kpi-label">{label}</span>
      {hint && <span className="auto-kpi-hint">{hint}</span>}
    </div>
  );
}

// Advanced blocks to surface (object-valued, non-delivery).
const ADVANCED_ORDER = [
  ["extraction", "Extraction"],
  ["rcc", "RCC"],
  ["arrm", "ARRM"],
  ["comparison", "Comparison"],
  ["reset", "Reset"],
  ["nudge", "Nudge"],
  ["sim", "SIM"],
  ["rolling", "Rolling"],
  ["login", "Login"],
  ["asset", "Asset"],
];

// ── main ─────────────────────────────────────────────────────────────────────

export default function AutomationTaskView({
  taskName,
  label,
  config,
  isLoading,
  totalRuns = null,
  canEdit = false,
  isAdmin = false,
  userLogin = "",
}) {
  // Run / Test / Run now: admin, or a contributor of this task (same as edit).
  const canRun = canEdit;
  const hasRunAudit = hasRunAuditTab(taskName);
  const qc = useQueryClient();
  const [tab, setTab] = useState("details"); // rapid tasks: details | audit
  React.useEffect(() => setTab("details"), [taskName]);
  const testTo = userLogin ? `${userLogin}@amazon.com` : "";
  // "running" shows while a slow task is executing in the background (the run
  // started on the server and we're polling for its result).
  const [running, setRunning] = useState(false);
  // Tables this task's report can send (null = task has no table selection).
  const { data: tablesData } = useQuery({
    queryKey: ["taskTables", taskName],
    queryFn: () => getTaskTables(taskName),
    enabled: !!taskName,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const tableOptions = tablesData?.supported ? tablesData.tables || [] : null;
  // Tables for the next manual Run / Test ([] = what each trigger is set to send).
  const [runTables, setRunTables] = useState([]);
  React.useEffect(() => setRunTables([]), [taskName]);
  const runTitles = runTables.map((k) => tableOptions?.find((o) => o.key === k)?.title || k);
  const testMutation = useMutation({
    mutationFn: () => runTaskTest(taskName, testTo, userLogin, () => setRunning(true), runTables),
    onMutate: () => setRunning(false),
    onSettled: () => {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["automationRunSummary"] });
    },
  });
  const rapidTestMutation = useMutation({
    mutationFn: () => runRapidTest(taskName, userLogin, () => setRunning(true), runTables),
    onMutate: () => setRunning(false),
    onSettled: () => {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["automationRunSummary"] });
    },
  });
  // "Run now": a real, LIVE run — sends to the actual recipients (mail) and
  // applies real changes (RAPID/RCC). dryRun:false distinguishes it from Test.
  const runNowMutation = useMutation({
    mutationFn: () => runTask(taskName, {
      dryRun: false, login: userLogin, onProgress: () => setRunning(true), tables: runTables,
    }),
    onMutate: () => setRunning(false),
    onSettled: () => {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["automationRunSummary"] });
      qc.invalidateQueries({ queryKey: ["automationRuns"] });
    },
  });
  const onRunNow = () => {
    if (
      window.confirm(
        `Run "${label || taskName}" for real now?\n\nThis is a LIVE run: it sends to the actual recipients` +
          (type === "rapid" ? " and applies real RCC changes" : "") +
          ". This is not a test." +
          (runTables.length
            ? `\n\nOnly these tables: ${runTitles.join(", ")}`
            : tableOptions ? "\n\nTables: what each trigger is set to send." : "")
      )
    ) {
      runNowMutation.mutate();
    }
  };
  // Local working copy so per-block saves reflect immediately without a refetch.
  const [cfg, setCfg] = useState(config || {});

  // Reset working copy when the task or incoming config changes.
  React.useEffect(() => {
    setCfg(config || {});
  }, [config, taskName]);

  if (!taskName) {
    return <div className="auto-empty">Select a task from the sidebar.</div>;
  }
  if (isLoading) {
    return <div className="auto-empty">Loading task…</div>;
  }

  const type = classifyTask(cfg, taskName);
  const title = label || cfg.label || prettyTaskLabel(taskName);
  // Report Design is piloting on SVR Inhouse only. Resolve its mapping (queue)
  // file from the config so the editor can read real work types / languages.
  const reportMapFile = cfg.assets?.queue_file || "siv_inhouse_queues.csv";
  const supportsReportDesign =
    taskName === "svr_inhouse_mail" || isKycDesign(taskName) || TABLE_DESIGN_TASKS.has(taskName);
  // Schedules live on the triggers. Each trigger's effective schedule (its own,
  // or a leftover task-level one that the backend moves onto it on save) drives
  // the Frequency KPI, the runs/day count and the General summary.
  const trigSchedules = getTriggers(cfg).map((t, i) => {
    const { schedules, inherited } = triggerSchedule(t, cfg);
    return { id: t.id || `trigger_${i + 1}`, schedules, inherited };
  });
  const scheduledTrigs = trigSchedules.filter((x) => x.schedules);
  const runsToday = taskRunsPerDay(cfg, getTriggers(cfg));
  const frequencyText = !scheduledTrigs.length
    ? (cfg.schedule && !legacyToSchedules(cfg.schedule) ? prettyFrequency(cfg.schedule) : "Not scheduled")
    : scheduledTrigs.length === 1
    ? shortSchedules(scheduledTrigs[0].schedules)
    : `${scheduledTrigs.length} trigger schedules`;
  const scheduleSummary = trigSchedules.length
    ? trigSchedules
        .map((x) => `${x.id}: ${x.schedules ? describeSchedules(x.schedules) : "not scheduled"}`)
        .join(" · ")
    : "—";
  const enabled = cfg.enabled;
  const maps = Array.isArray(cfg.maps) ? cfg.maps : [];
  const deliveries = deliveryCount(cfg);
  const deliveryWord = type === "mail" ? "Mails / run" : "Posts / run";
  const saved = timeSaved(cfg, totalRuns);
  const hasConfig = !!config;

  // Merge a partial patch into the full config and persist to S3.
  const persist = async (nextFull) => {
    await saveTaskConfig(taskName, nextFull);
    setCfg(nextFull);
    qc.invalidateQueries({ queryKey: ["taskConfig", taskName] });
    qc.invalidateQueries({ queryKey: ["automationGroups"] });
    qc.invalidateQueries({ queryKey: ["taskTables", taskName] }); // report design may add/rename tables
  };

  // Save a single top-level key (e.g. "extraction") with its new value.
  const saveKey = (key) => async (nextValue) => {
    await persist({ ...cfg, [key]: nextValue });
  };

  // Save the General block (a subset of top-level fields). Schedules are not
  // part of it — they are edited on each trigger.
  const saveGeneral = async (draft) => {
    await persist({ ...cfg, ...draft });
  };

  // Save a single trigger by index.
  const saveTrigger = (idx) => async (nextTrigger) => {
    const triggers = [...getTriggers(cfg)];
    // Strip the delivery-toggle scratch caches and any emptied delivery block so
    // the stored config stays clean (delivery is presence-driven: email XOR slack).
    const clean = { ...nextTrigger };
    delete clean._emailCache;
    delete clean._slackCache;
    delete clean._schedCache;
    delete clean._inheritedSchedule;
    if (clean.email === undefined) delete clean.email;
    if (clean.slack === undefined) delete clean.slack;
    if (clean.schedules) clean.schedules = cleanSchedules(clean.schedules);
    else delete clean.schedules;
    triggers[idx] = clean;
    const next = { ...cfg, triggers };
    // Move a leftover task-level schedule onto the OTHER triggers now, so this
    // trigger's choice (including "not scheduled") isn't overwritten on save.
    if (cfg.schedules || legacyToSchedules(cfg.schedule)) {
      next.triggers = triggers.map((t, j) =>
        j === idx || t.schedules ? t : { ...t, schedules: triggerSchedule(t, cfg).schedules });
      delete next.schedules;
      delete next.schedule;
    }
    await persist(next);
  };

  // Add / remove triggers, e.g. one "email" trigger and one "slack" trigger so
  // each channel can have its own schedule.
  const addTrigger = async (kind) => {
    const existing = Array.isArray(cfg.triggers) ? cfg.triggers : getTriggers(cfg);
    const ids = new Set(existing.map((t, i) => t.id || `trigger_${i + 1}`));
    let id = kind;
    for (let n = 2; ids.has(id); n += 1) id = `${kind}_${n}`;
    const schedules = defaultSchedules();
    const trig = kind === "slack"
      ? { id, slack: { channel_id: "", comment: "" }, schedules }
      : { id, email: { from_address: "no-reply@pulse-cia.amazon.dev", display_only: false, mode: "flat",
                       subject: cfg.label || "", to: "", cc: "", bcc: "" }, schedules };
    await persist({ ...cfg, triggers: [...existing, trig] }); // backend moves any task-level schedule onto the others
  };
  const removeTrigger = async (idx) => {
    const all = getTriggers(cfg);
    const t = all[idx] || {};
    if (!window.confirm(`Remove trigger "${t.id || `trigger_${idx + 1}`}"? Its recipients/channel and schedule are removed from the config (revertible from the Activity Log).`)) return;
    await persist({ ...cfg, triggers: all.filter((_, i) => i !== idx) });
  };


  const generalDraftInit = {
    label: cfg.label ?? "",
    description: cfg.description ?? "",
    enabled: typeof cfg.enabled === "boolean" ? cfg.enabled : true,
    start_date: cfg.start_date ?? "",
    minutes_saved_per_run: cfg.minutes_saved_per_run ?? 0,
    section: cfg.section ?? "",
    section_order: cfg.section_order ?? 0,
    order: cfg.order ?? 0,
  };

  // Tasks saved before triggers existed get one synthesized "default" trigger,
  // so every task has a trigger to put its schedule on.
  const triggers = getTriggers(cfg);
  const groups = Array.isArray(cfg.groups) ? cfg.groups : [];

  return (
    <div className="auto-task">
      <div className="auto-task-header">
        <div className="auto-main-title-row">
          <h2 className="auto-task-title">{title}</h2>
          <span className={`auto-type-badge ${(TYPE_META[type] || TYPE_META.generic).cls}`}>
            {(TYPE_META[type] || TYPE_META.generic).label}
          </span>
          {typeof enabled === "boolean" && (
            <span className={`auto-state ${enabled ? "auto-state--on" : "auto-state--off"}`}>
              <span className="auto-state-dot" />
              {enabled ? "Enabled" : "Disabled"}
            </span>
          )}
          {canRun && type === "mail" && testTo && (
            <button
              className="auto-test-btn"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              title={`Run now and send the email ONLY to you (${testTo}) — real recipients get nothing`}
            >
              {testMutation.isPending ? (
                <RefreshCw size={14} className="auto-eb-spin" />
              ) : (
                <Send size={14} />
              )}
              {testMutation.isPending ? (running ? "Running…" : "Starting…") : "Test run"}
            </button>
          )}
          {canRun && (type === "rapid" || type === "slack") && (
            <button
              className="auto-test-btn"
              onClick={() => rapidTestMutation.mutate()}
              disabled={rapidTestMutation.isPending}
              title={
                type === "rapid"
                  ? "Dry run (no RCC changes) — posts the report to the test Slack channel"
                  : "Run now — posts the report to the test Slack channel"
              }
            >
              {rapidTestMutation.isPending ? (
                <RefreshCw size={14} className="auto-eb-spin" />
              ) : (
                <Play size={14} />
              )}
              {rapidTestMutation.isPending ? (running ? "Running…" : "Starting…") : "Test run"}
            </button>
          )}
          {canRun && (isAdmin || cfg.live_runs !== "admin") && (
            <button
              className="auto-test-btn auto-run-now-btn"
              onClick={onRunNow}
              disabled={
                runNowMutation.isPending || testMutation.isPending || rapidTestMutation.isPending
              }
              title={
                type === "rapid"
                  ? "LIVE run — applies real RCC changes and posts the report to the live channel"
                  : type === "mail"
                  ? "LIVE run — sends the email to the real recipients now"
                  : "LIVE run — runs the report now and delivers to the real destination"
              }
            >
              {runNowMutation.isPending ? (
                <RefreshCw size={14} className="auto-eb-spin" />
              ) : (
                <Zap size={14} />
              )}
              {runNowMutation.isPending ? (running ? "Running…" : "Starting…") : "Run now"}
            </button>
          )}
        </div>
        <p className="auto-task-subtitle">
          <code className="auto-main-code">{taskName}</code>
        </p>
        {canRun && tableOptions && tableOptions.length > 1 && (
          <div className="auto-run-tables">
            <span className="auto-eb-field-label">Run / Test with</span>
            <TablePicker
              options={tableOptions}
              value={runTables}
              onChange={setRunTables}
              allLabel="Trigger settings"
              disabled={runNowMutation.isPending || testMutation.isPending || rapidTestMutation.isPending}
            />
            <span className="auto-eb-field-hint">
              {runTables.length
                ? `Next Run now / Test run sends only: ${runTitles.join(", ")}`
                : "Each trigger sends the tables it is set to (Tables in the trigger). Pick tables to send only those this time."}
            </span>
          </div>
        )}
        {canRun &&
          (testMutation.isPending || rapidTestMutation.isPending || runNowMutation.isPending) && (
          <div className="auto-test-result auto-test-result--run">
            <RefreshCw size={14} className="auto-eb-spin" />
            <span>
              {running
                ? "Running… this can take a few minutes for large pulls. You can leave this page — the result lands in Recent Runs."
                : "Starting the run…"}
            </span>
          </div>
        )}
        {canRun && type === "mail" && !testMutation.isPending && (testMutation.data || testMutation.isError) && (
          <div
            className={`auto-test-result ${
              testMutation.isError || !testMutation.data?.success
                ? "auto-test-result--err"
                : "auto-test-result--ok"
            }`}
          >
            {testMutation.isError || !testMutation.data?.success ? (
              <XCircle size={14} />
            ) : (
              <CheckCircle2 size={14} />
            )}
            <span>
              {testMutation.isError
                ? testMutation.error?.message || "Test failed"
                : testMutation.data?.success
                ? `Test sent to ${testTo}`
                : testMutation.data?.summary || "Test failed"}
            </span>
          </div>
        )}
        {canRun && (type === "rapid" || type === "slack") && !rapidTestMutation.isPending && (rapidTestMutation.data || rapidTestMutation.isError) && (
          <div
            className={`auto-test-result ${
              rapidTestMutation.isError || !rapidTestMutation.data?.success
                ? "auto-test-result--err"
                : "auto-test-result--ok"
            }`}
          >
            {rapidTestMutation.isError || !rapidTestMutation.data?.success ? (
              <XCircle size={14} />
            ) : (
              <CheckCircle2 size={14} />
            )}
            <span>
              {rapidTestMutation.isError
                ? rapidTestMutation.error?.message || "Test failed"
                : rapidTestMutation.data?.summary ||
                  (rapidTestMutation.data?.success
                    ? "Report posted to test channel"
                    : "Test failed")}
            </span>
          </div>
        )}
        {canRun && !runNowMutation.isPending && (runNowMutation.data || runNowMutation.isError) && (
          <div
            className={`auto-test-result ${
              runNowMutation.isError || !runNowMutation.data?.success
                ? "auto-test-result--err"
                : "auto-test-result--ok"
            }`}
          >
            {runNowMutation.isError || !runNowMutation.data?.success ? (
              <XCircle size={14} />
            ) : (
              <CheckCircle2 size={14} />
            )}
            <span>
              {runNowMutation.isError
                ? runNowMutation.error?.message || "Run failed"
                : runNowMutation.data?.summary ||
                  (runNowMutation.data?.success ? "Live run complete" : "Run failed")}
            </span>
          </div>
        )}
      </div>

      {!hasConfig && (
        <div className="auto-notice">
          No live config returned by the dispatcher for this task. Sign in and
          confirm the automation endpoint is reachable to populate this.
        </div>
      )}

      {(type === "rapid" || hasRunAudit) && (
        <div className="auto-subtabs">
          <button
            className={`auto-subtab ${tab === "details" ? "auto-subtab--active" : ""}`}
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            className={`auto-subtab ${tab === "audit" ? "auto-subtab--active" : ""}`}
            onClick={() => setTab("audit")}
          >
            Audit
          </button>
        </div>
      )}

      {type === "rapid" && tab === "audit" ? (
        <RapidAudit taskName={taskName} />
      ) : hasRunAudit && tab === "audit" ? (
        <TemplateResetAudit taskName={taskName} />
      ) : (
      <>
      {/* ── KPI strip ── */}
      <div className="auto-kpi-strip">
        <Kpi
          label="Total runs"
          value={totalRuns == null ? "—" : totalRuns.toLocaleString()}
          hint={totalRuns == null ? "pending run history" : null}
        />
        <Kpi
          label="Time saved"
          value={formatMinutes(saved)}
          hint={cfg.minutes_saved_per_run ? `${cfg.minutes_saved_per_run} min/run` : null}
        />
        <Kpi label="Start date" value={formatStartDate(cfg.start_date)} />
        <Kpi
          label="Frequency"
          value={frequencyText}
          hint={scheduledTrigs.length ? `${runsToday} run${runsToday === 1 ? "" : "s"} today` : null}
        />
        <Kpi label="Maps" value={maps.length} />
        <Kpi label={deliveryWord} value={deliveries} />
      </div>

      {/* ── Description ── */}
      <div className="auto-desc">{cfg.description || "No description."}</div>

      {/* ── Config | Report Design sub-tabs (SVR Inhouse pilot) ── */}
      {supportsReportDesign && hasConfig && (
        <div className="auto-subtabs">
          <button
            className={`auto-subtab ${tab !== "report" ? "auto-subtab--active" : ""}`}
            onClick={() => setTab("details")}
          >
            Configuration
          </button>
          <button
            className={`auto-subtab ${tab === "report" ? "auto-subtab--active" : ""}`}
            onClick={() => setTab("report")}
          >
            Report Design
          </button>
        </div>
      )}

      {supportsReportDesign && tab === "report" && hasConfig && (
        isKycDesign(taskName) ? (
          <KycReportDesignBlock
            taskName={taskName}
            config={cfg}
            canEdit={canEdit}
            onSave={persist}
          />
        ) : TABLE_DESIGN_TASKS.has(taskName) ? (
          <TableReportDesignBlock
            taskName={taskName}
            config={cfg}
            canEdit={canEdit}
            onSave={persist}
          />
        ) : (
          <ReportDesignBlock
            taskName={taskName}
            config={cfg}
            mapFile={reportMapFile}
            canEdit={canEdit}
            onSave={persist}
          />
        )
      )}

      {hasConfig && !(supportsReportDesign && tab === "report") && (
        <>
          {/* ── General (editable) ── */}
          <EditableBlock
            title="General"
            value={generalDraftInit}
            editable={canEdit}
            onSave={saveGeneral}
            renderEdit={(draft, setDraft) => (
              <div className="auto-eb-grid">
                <AutoField label="label" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
                <AutoField label="enabled" value={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} />
                <p className="auto-eb-field-hint auto-eb-field--wide auto-sched2-note">
                  Schedules are set on each trigger below (Edit a trigger → Schedule).
                </p>
                <label className="auto-eb-field">
                  <span className="auto-eb-field-label">start_date</span>
                  <input
                    className="auto-eb-input"
                    type="date"
                    value={String(draft.start_date || "").slice(0, 10)}
                    onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                  />
                </label>
                <AutoField label="minutes_saved_per_run" value={draft.minutes_saved_per_run} onChange={(v) => setDraft({ ...draft, minutes_saved_per_run: v })} />
                <AutoField label="section" value={draft.section} onChange={(v) => setDraft({ ...draft, section: v })} />
                <AutoField label="section_order" value={draft.section_order} onChange={(v) => setDraft({ ...draft, section_order: v })} />
                <AutoField label="order" value={draft.order} onChange={(v) => setDraft({ ...draft, order: v })} />
                <AutoField label="description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} />
              </div>
            )}
            renderView={() => (
              <dl className="auto-cfg-kv">
                {Object.entries(generalDraftInit).map(([k, v]) => (
                  <div className="auto-cfg-kv-row" key={k}>
                    <dt>{k}</dt>
                    <dd>{k === "start_date" ? formatStartDate(v) : String(v)}</dd>
                  </div>
                ))}
                <div className="auto-cfg-kv-row">
                  <dt>schedule</dt>
                  <dd>
                    {scheduleSummary}
                    {scheduledTrigs.length ? ` (${runsToday} run${runsToday === 1 ? "" : "s"} today)` : ""}
                  </dd>
                </div>
              </dl>
            )}
          />


          {/* ── Triggers (each editable, proper audience fields) ── */}
          {triggers.map((raw, i) => {
            // Show/edit the trigger with its effective schedule (a leftover
            // task-level one is moved onto the trigger when saved).
            const eff = triggerSchedule(raw, cfg);
            const t = eff.inherited ? { ...raw, schedules: eff.schedules, _inheritedSchedule: true } : raw;
            return (
            <EditableBlock
              key={i}
              title={`Trigger: ${t.id || `trigger_${i + 1}`}`}
              subtitle={`· ${[t.email && "email", t.slack && "slack"].filter(Boolean).join(" + ") || "no delivery"}${
                t.schedules ? ` · ${shortSchedules(t.schedules)}` : " · not scheduled"}`}
              value={t}
              editable={canEdit}
              onSave={saveTrigger(i)}
              renderView={() => <TriggerView trig={t} tableOptions={tableOptions} />}
              renderEdit={(draft, setDraft) => (
                <TriggerEdit draft={draft} setDraft={setDraft} tableOptions={tableOptions} />
              )}
            />
            );
          })}
          {canEdit && (
            <div className="auto-trig-bar">
              <span className="auto-eb-field-hint">
                Triggers: add one per audience. Give a trigger its own schedule to send email and Slack at different times.
              </span>
              <button type="button" className="auto-sched2-add" onClick={() => addTrigger("email")}>
                <Plus size={13} /> Email trigger
              </button>
              <button type="button" className="auto-sched2-add" onClick={() => addTrigger("slack")}>
                <Plus size={13} /> Slack trigger
              </button>
              {triggers.length > 1 &&
                triggers.map((t, i) => (
                  <button
                    key={`rm-${i}`}
                    type="button"
                    className="auto-sched2-add"
                    aria-label={`Remove trigger ${t.id || `trigger_${i + 1}`}`}
                    onClick={() => removeTrigger(i)}
                  >
                    <Trash2 size={13} /> {t.id || `trigger_${i + 1}`}
                  </button>
                ))}
            </div>
          )}

          {/* ── Advanced object blocks (each editable) ── */}
          {ADVANCED_ORDER.filter(([k]) => cfg[k] && typeof cfg[k] === "object").map(
            ([k, lbl]) => (
              <EditableBlock key={k} title={lbl} value={cfg[k]} editable={canEdit} onSave={saveKey(k)} />
            )
          )}

          {/* ── queue_types / groups (arrays of objects) ── */}
          {Array.isArray(cfg.queue_types) && cfg.queue_types.length > 0 && (
            <EditableBlock title="Queue types" value={cfg.queue_types} editable={canEdit} onSave={saveKey("queue_types")} />
          )}
          {groups.length > 0 && (
            <EditableBlock title="Groups" value={cfg.groups} editable={canEdit} onSave={saveKey("groups")} />
          )}

          {/* ── Mapping (per-CSV download / edit grid / replace) ── */}
          <div className="auto-cfg-block">
            <h4 className="auto-cfg-block-title">
              Mapping
              {maps.length ? (
                <span className="auto-eb-sub"> · {maps.length} file{maps.length === 1 ? "" : "s"}</span>
              ) : null}
            </h4>
            <MappingEditor taskName={taskName} maps={maps} canEdit={canEdit} />
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
